import ArrowRightIcon from "assets/svg/arrow-right-icon";
import ExportIcon from "assets/svg/export-icon";
import HeartIcon from "assets/svg/heart-icon";
import MapPinIcon from "assets/svg/map-pin-icon";
import Button from "components/button/Button";
import GeneralSharePopup from "components/sharePopup/GeneralSharePopup";
import { useWishlist } from "contexts/WishlistContext";
import React, { useState, useRef, useEffect } from "react";
//import { useNavigate } from "react-router-dom";
import { getBillingFrequencyFormat } from "utils/getBillingFrequencyFormat";
import { getCurrency } from "utils/getCurrency";
import { getFormattedPrice } from "utils/getFormattedPrice";
import { getImageUrl } from "utils/getImageUrl";

const LocationsPopperMenu = ({ cityData, setIsLocationsOpen }) => {
  const [hoveredItem, setHoveredItem] = useState(null);
  const [hoveredType, setHoveredType] = useState(null);
  const [showSharePopup, setShowSharePopup] = useState(false);
  const [isFirstImageLoaded, setIsFirstImageLoaded] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const { isWishListed, toggleWishlist } = useWishlist();

  const containerRef = useRef(null);
  const leftSectionRef = useRef(null);
  const rightSectionRef = useRef(null);
  const timeoutRef = useRef(null);
  //const navigate = useNavigate();

  useEffect(() => {
    const timer = setTimeout(() => {
      setIsVisible(true);
    }, 10);

    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    if (cityData?.length > 0) {
      const firstCity = cityData[0];
      const firstCityImageUrl = getImageUrl(
        firstCity?.hero_media?.formats,
        "medium"
      );

      if (firstCityImageUrl) {
        const img = new Image();
        img.onload = () => {
          setIsFirstImageLoaded(true);
        };
        img.onerror = () => {
          setIsFirstImageLoaded(true);
        };
        img.src = firstCityImageUrl;
      } else {
        setIsFirstImageLoaded(true);
      }
    }
  }, [cityData]);

  useEffect(() => {
    if (cityData?.length > 0 && !hoveredItem && isFirstImageLoaded) {
      setHoveredItem(cityData[0]);
      setHoveredType("city");
    }
  }, [cityData, isFirstImageLoaded]);

  const handleCityMouseEnter = (city) => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    setHoveredItem(city);
    setHoveredType("city");
  };

  const handleSharePopupToggle = () => {
    setShowSharePopup((p) => !p);
  };

  const handlePropertyMouseEnter = (property) => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    setHoveredItem(property);
    setHoveredType("property");
  };

  const handleContainerMouseEnter = () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  };

  const handleToggleFavorite = async () => {
    if (!hoveredItem?.slug || hoveredType !== "property") return;

    toggleWishlist({
      id: `property-${hoveredItem?.slug}`,
      propertySlug: hoveredItem?.slug,
      roomTypeSlug: null,
    });
  };

  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  const renderRightSection = () => {
    if (!hoveredItem) return null;

    const isCity = hoveredType === "city";
    const media = isCity ? hoveredItem?.hero_media : hoveredItem?.media?.[0];
    const roomCategories = isCity
      ? hoveredItem?.properties?.flatMap(
          (property) => property?.room_categories || []
        )
      : hoveredItem?.room_categories;

    const uniqueRoomCategories =
      roomCategories?.filter(
        (category, index, self) =>
          index === self.findIndex((c) => c?.id === category?.id)
      ) || [];
    const name = hoveredItem?.name;
    const price = getFormattedPrice(hoveredItem?.room_starting_price);
    const currency = getCurrency(hoveredItem?.room_starting_price_currency);
    const billingFrequency = getBillingFrequencyFormat(
      hoveredItem?.room_starting_price_billing_frequency
    );

    const address = isCity ? null : hoveredItem?.address;

    const getPopupFormattedData = () => {
      return {
        ...hoveredItem,
        image:
          hoveredType === "city"
            ? {
                url: getImageUrl(
                  hoveredItem?.hero_media?.formats,
                  "medium",
                  hoveredItem?.hero_media?.url
                ),
                name: hoveredItem?.hero_media?.formats?.name,
              }
            : {
                url: getImageUrl(
                  hoveredItem?.media?.[0]?.formats,
                  "medium",
                  hoveredItem?.media?.[0]?.url
                ),
                name: hoveredItem?.media?.[0]?.name,
              },
        address:
          hoveredType === "city"
            ? `${hoveredItem?.name ?? ""} ${hoveredItem?.country?.name ?? ""}`
            : `${hoveredItem?.address?.line1 ?? ""} ${
                hoveredItem?.address?.city ?? ""
              }`,
      };
    };

    const getShareUrl = () => {
      const baseUrl = window.location.origin;
      if (hoveredType === "city") {
        return `${baseUrl}/${hoveredItem?.slug}`;
      } else {
        return `${baseUrl}/${hoveredItem?.city_slug}/${hoveredItem?.slug}`;
      }
    };

    const getWhatsappShareMessage = () => {
      if (hoveredType === "city") {
        return `Check out properties in ${hoveredItem?.name ?? "this City"}!`;
      } else {
        return `Check out ${hoveredItem?.name ?? "this Room"}!`;
      }
    };

    const getSubText = () => {
      const currency = getCurrency(hoveredItem?.room_starting_price_currency);
      const price = getFormattedPrice(hoveredItem?.room_starting_price);
      const billingFrequency = getBillingFrequencyFormat(
        hoveredItem?.room_starting_price_billing_frequency
      );

      return (
        <p className="text-pure-white text-xs">
          {hoveredType === "city" ? "Rooms starting" : ""} From {currency}
          {price} /per {billingFrequency}
          <span className="italic text-[#EBDFAB]"> (All-bills Inc.)</span>
        </p>
      );
    };

    return (
      <div
        ref={rightSectionRef}
        className="w-[75vw]"
        onMouseEnter={handleContainerMouseEnter}
      >
        <div className="h-full w-full relative overflow-hidden rounded-r-[8px]">
          <img
            src={getImageUrl(media?.formats, "large")}
            alt={name}
            className="absolute inset-0 w-full h-full object-cover"
          />

          <div
            className="absolute inset-0"
            style={{
              background:
                "linear-gradient(180deg, rgba(0, 0, 0, 0) 18.49%, rgba(0, 0, 0, 0.95) 100%)",
              mixBlendMode: "normal",
            }}
          />

          <div className="relative z-10 h-full flex flex-col justify-end">
            <div className="px-[16px] py-[24px] 2xl:px-[36px] 2xl:py-[36px] flex flex-col space-y-[2px]">
              {address && (
                <div className="flex items-center gap-[4px] text-[14px] text-pure-white font-normal">
                  <MapPinIcon className="w-[20px] h-[20px] shrink-0" />
                  <p>{address?.line1}</p>
                  {address?.line2 && <p>{address?.line2}</p>}
                </div>
              )}

              <h3 className="text-[48px] italic font-pfd font-bold text-pure-white">
                {name}
              </h3>

              <div className="flex items-center gap-[6px]">
                {uniqueRoomCategories?.map((roomCategory, index) => (
                  <div
                    key={roomCategory?.id}
                    className="flex items-center gap-[6px] text-tint-shades"
                  >
                    <p className=" text-[16px] font-medium">
                      {roomCategory?.name}
                    </p>
                    {index !== uniqueRoomCategories.length - 1 && (
                      <span className="text-[16px] font-medium">•</span>
                    )}
                  </div>
                ))}
              </div>

              <div className="flex items-center gap-[16px] 2xl:gap-[24px] justify-between w-full">
                <div className="text-pure-white">
                  {price && (
                    <>
                      {" "}
                      <span className="text-[16px] font-normal">From</span>{" "}
                      <span className="text-[24px] font-bold">
                        {currency}
                        {price}{" "}
                        <span className="text-[16px] font-normal">
                          /per {billingFrequency}
                        </span>
                      </span>{" "}
                      <span className="text-[16px] font-bold text-tint-shades italic">
                        (All-bills Inc.)
                      </span>
                    </>
                  )}
                </div>

                <div className="flex items-center gap-[16px] 2xl:gap-[24px]">
                  {!isCity && (
                    <div className="flex items-center gap-[14px] 2xl:gap-[18px]">
                      <button
                        className="group flex items-center gap-[4px]"
                        onClick={handleToggleFavorite}
                      >
                        <HeartIcon
                          className={`group-hover:fill-secondary-rectangle-one w-[20px] h-[20px] text-pure-white transition-colors duration-200 ${
                            isWishListed(`property-${hoveredItem?.slug}`)
                              ? "text-secondary-rectangle-one fill-secondary-rectangle-one"
                              : "fill-transparent"
                          }`}
                        />
                      </button>

                      <button
                        className="group flex items-center gap-[4px]"
                        onClick={handleSharePopupToggle}
                      >
                        <ExportIcon className="w-[20px] h-[20px] text-pure-white group-hover:text-secondary-rectangle-one" />
                      </button>
                    </div>
                  )}

                  <Button
                    onClick={() => {
                      if (
                        hoveredItem?.name?.toLowerCase()?.includes("cambridge")
                      ) {
                        // window.open(
                        //   `${process.env.REACT_APP_RAILYARD_BASE_URL}/`,
                        //   "_blank"
                        // );
                        window.open(`/`, "_blank");
                        return;
                      }
                      if (isCity) {
                        window.open(`/`, "_blank");
                        // navigate(`/${hoveredItem?.slug}`);
                        setIsLocationsOpen(false);
                      } else {
                        window.open(
                          `/${hoveredItem?.city_slug}/${hoveredItem?.slug}`,
                          "_blank"
                        );
                        // navigate(
                        //   `/${hoveredItem?.city_slug}/${hoveredItem?.slug}`
                        // );
                      }
                      setIsLocationsOpen(false);
                    }}
                    suffixIcon={ArrowRightIcon}
                    iconType="svg"
                    iconClass="w-0 h-0 group-hover:w-[16px] group-hover:h-[16px] text-transparent group-hover:text-pure-white shrink-0"
                    innerText={isCity ? "Explore City" : "View Property"}
                    innerClass={`font-bold  text-[12px] lg:text-[14px] text-pure-white`}
                    outerClass={`flex items-center h-[50px] w-[190px] justify-center gap-2 group relative hover:bg-secondary-rectangle-one sheen border border-pure-white hover:border-secondary-rectangle-one rounded-[8px] px-[12px] py-[8px] lg:px-[18px] 2xl:px-[28px] 2xl:py-[12px] bg-transparent`}
                  />
                </div>
                <GeneralSharePopup
                  isOpen={showSharePopup}
                  onClose={handleSharePopupToggle}
                  data={getPopupFormattedData()}
                  getShareUrl={getShareUrl}
                  getWhatsappShareMessage={getWhatsappShareMessage}
                  getSubText={getSubText}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const handleCityClick = (city) => {
    window.open(`/`, "_blank");
    // navigate(`/${city?.slug}`);
    setIsLocationsOpen(false);
  };

  const handlePropertyClick = (city, property) => {
    window.open(`/${city?.slug}/${property?.slug}`, "_blank");
    // navigate(`/${city?.slug}/${property?.slug}`);
    setIsLocationsOpen(false);
  };

  return (
    <>
      <div
        ref={containerRef}
        className={`w-full h-[70vh] rounded-[8px] shadow-locations-popper-menu-shadow bg-secondary-bg transform transition-all duration-300 ease-out ${
          isVisible ? "translate-y-0 opacity-100" : "-translate-y-4 opacity-0"
        }`}
        onMouseEnter={handleContainerMouseEnter}
      >
        <div className="flex h-full">
          <div
            ref={leftSectionRef}
            className="w-[25vw] shrink-0 px-[28px] py-[16px] 2xl:px-[36px] flex flex-col"
          >
            <button
              onClick={() => window.open(`/city-listing`, "_blank")}
              // onClick={() => navigate("/city-listing")}
              className="text-left text-[18px] font-medium text-primary-black hover:text-secondary-rectangle-one underline underline-offset-[4px] mb-6"
            >
              All Cities
            </button>

            <div className="flex-1 overflow-y-auto no-scrollbar">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-y-[24px] relative">
                {/*<div className=" bg-secondary-gray hidden md:block absolute top-0 bottom-0 left-[35%] w-px z-10"></div>*/}

                {cityData?.length > 0 &&
                  cityData?.map((city, index) => {
                    if (city?.isVisible === false) {
                      return null;
                    }
                    return (
                      <div key={city.id} className={`break-inside-avoid`}>
                        <button
                          onClick={() => {
                            handleCityClick(city);
                            setIsLocationsOpen(false);
                            return;
                          }}
                        >
                          <h3
                            className="text-[18px] font-medium text-primary-black cursor-pointer hover:underline hover:underline-offset-[4px] hover:text-secondary-rectangle-one transition-colors"
                            onMouseEnter={() => handleCityMouseEnter(city)}
                          >
                            {city?.name}
                          </h3>
                        </button>

                        {city?.properties && city?.properties?.length > 0 && (
                          <div className="w-fit flex flex-col gap-2 mt-1">
                            {city?.properties?.map((property) => (
                              <button
                                key={property.id}
                                className="text-[14px] text-left text-primary-black font-medium cursor-pointer hover:underline hover:underline-offset-[4px] hover:text-secondary-rectangle-one transition-colors "
                                onMouseEnter={() =>
                                  handlePropertyMouseEnter({
                                    ...property,
                                    city_slug: city?.slug ?? undefined,
                                  })
                                }
                                onClick={() => {
                                  if (
                                    property?.name
                                      .toLowerCase()
                                      .includes("square")
                                  ) {
                                    window.open(
                                      `/manchester/square-gardens`,
                                      "_blank"
                                    );
                                    // navigate(`/manchester/square-gardens`);
                                    setIsLocationsOpen(false);
                                    return;
                                  }
                                  handlePropertyClick(city, property);
                                }}
                              >
                                {property?.name}
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  })}
              </div>
            </div>
          </div>

          {renderRightSection()}
        </div>
      </div>
    </>
  );
};

export default LocationsPopperMenu;
